import { useState } from 'react'

import { Card } from '@taro/cards'
import { Stack, Surface, Text } from '@taro/design'
import { InkReveal } from '@taro/ink/react'
import { haptic } from '@taro/tg'

import type { Daily as DailyData } from '../api'
import { useDeviceTilt } from '../useDeviceTilt'
import styles from './Daily.module.css'

interface Props {
  daily: DailyData
  onReveal: () => void
}

/**
 * Карта дня.
 *
 * Раскрывается по нажатию, а не сама: переворот — это маленький ритуал,
 * и отдавать его анимации значит превращать карту в баннер.
 */
export function Daily({ daily, onReveal }: Props) {
  const [open, setOpen] = useState(daily.revealed)

  // Фольга живёт на РУБАШКЕ и гаснет, как только карта открыта.
  //
  // Так она делает две вещи разом: закрытая карта выглядит предметом,
  // который хочется взять в руки, а открытая не спорит переливом
  // с иллюстрацией. Заодно гироскоп выключается сразу после переворота
  // и не тратит батарею всё время, пока читают значение.
  const tilt = useDeviceTilt(!open)

  const reveal = () => {
    if (open) return
    setOpen(true)
    haptic('tap')
    onReveal()
  }

  const meaning = daily.isReversed ? daily.card.reversed : daily.card.upright

  return (
    <Surface level="base" sacred className={styles.box}>
      <Stack gap={5} align="center">
        <Text variant="overline" tone="tertiary">
          Карта дня
        </Text>

        <Card
          back="/card-back.webp"
          face={daily.card.image}
          label={open ? daily.card.name : 'Открыть карту дня'}
          size="xl"
          revealed={open}
          reversed={daily.isReversed}
          holo={!open}
          tilt={tilt}
          onSelect={open ? undefined : reveal}
        />

        {/* Значение — объект внутри экрана, поэтому дыхание, а не чернила:
            смена экрана это событие, а раскрытие блока — нет. Уровень
            задаётся по умолчанию, здесь его называть не нужно. */}
        <InkReveal show={open} appear={!daily.revealed}>
          <Stack gap={4} align="center" className={styles.reading}>
            <Stack gap={1} align="center">
              <Text variant="title-l" as="h2" style={{ textAlign: 'center' }}>
                {daily.card.name}
              </Text>
              {daily.isReversed ? (
                <Text variant="overline" tone="tertiary">
                  Перевёрнутая
                </Text>
              ) : null}
            </Stack>

            {/* Значение — то, ради чего карту переворачивают. Описание карты
                идёт ниже и мельче: это справка, а не сегодняшний смысл. */}
            <div className="oracle">
              <p className="oracle__lead">{meaning}</p>
            </div>

            <div className={styles.divider} />

            <Text variant="body-m" tone="tertiary" measure>
              {daily.card.description}
            </Text>
          </Stack>
        </InkReveal>

        <InkReveal show={!open}>
          <Text variant="body-m" tone="tertiary">
            Нажми, чтобы перевернуть
          </Text>
        </InkReveal>
      </Stack>
    </Surface>
  )
}
